import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useOfferEventSuggestionsMutation, useUpdateEventSuggestionMutation } from '@graphql/generated';
import { useDisclosure } from '@withjoy/joykit';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';

const PAYMENT_METHOD_DISCLAIMER_SIGNED = 'paymentMethodDisclaimerSignedList';
export interface PaymentMethodDisclaimerSignedMetadata {
  paymentMethod: string;
  paymentUsername: string;
  paymentLink: string;
  timestamp: number;
  userId: string;
}

export function useAppPaymentDisclaimerDialogController(eventId: string) {
  const [offerEventSuggestionsMutation, { data, loading }] = useOfferEventSuggestionsMutation();
  const paymentMethodsDisclaimerSigned = useMemo(() => data?.offerEventSuggestions.at(0), [data]);
  const [updateEventSuggestionMutation] = useUpdateEventSuggestionMutation();
  const [paymentMetadata, setPaymentMetadata] = useState<PaymentMethodDisclaimerSignedMetadata | null>(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const telemetry = useAdminRegistryTelemetry();
  const disclaimerFormik = useFormik<{ agreement: string }>({
    initialValues: { agreement: '' },
    onSubmit: async (values, { resetForm }) => {
      if (!paymentMethodsDisclaimerSigned?.id || loading) return;
      telemetry.acceptCashFundDisclaimerClicked();

      await updateEventSuggestionMutation({
        variables: {
          id: paymentMethodsDisclaimerSigned.id,
          payload: {
            stateJSON: { paymentMethodsDisclaimerSignedList: paymentMetadata }
          }
        }
      });
      setPaymentMetadata(null);
      setAgreementAccepted(true);
      resetForm();
      onClose();
    },
    validationSchema: Yup.object().shape({
      agreement: Yup.string()
        .required('This field is required')
        .test('is-i-agree', 'Please type “I AGREE” in the text field above to continue.', value => typeof value === 'string' && value.trim().toLowerCase() === 'i agree')
    })
  });

  const { onOpen, onClose, isOpen } = useDisclosure();

  useEffect(() => {
    offerEventSuggestionsMutation({
      variables: {
        eventId,
        identifiers: [PAYMENT_METHOD_DISCLAIMER_SIGNED]
      }
    });
  }, [offerEventSuggestionsMutation, eventId]);

  const onOpenDisclaimerDialog = (paymentMetadata: PaymentMethodDisclaimerSignedMetadata) => {
    setPaymentMetadata(paymentMetadata);
    onOpen();
  };

  return { onOpenDisclaimerDialog, onClose, isOpen, disclaimerFormik, agreementAccepted, setAgreementAccepted };
}
